import { createApp, h, reactive } from 'vue'
import AlertDialog from '@/components/ui/AlertDialog.vue'
import { ErrorCategory, ErrorSeverity, AppError } from '@/utils/errorUtils'
import i18n from '@/i18n'

const { t } = i18n.global

interface AlertDialogOptions {
  title: string
  message: string
  type?: 'error' | 'warning' | 'info' | 'network'
  showIcon?: boolean
  showRetryButton?: boolean
  retryButtonText?: string
  closeButtonText?: string
  closeOnBackdrop?: boolean
  autoClose?: boolean
  duration?: number
  onRetry?: () => void
  onClose?: () => void
}

interface AlertDialogInstance {
  id: number
  el: HTMLElement
  close: () => void
}

// Keep track of active alert dialogs
const alertDialogs = reactive<Record<number, AlertDialogInstance>>({})
let alertDialogIdCounter = 0

/**
 * Show an alert dialog
 */
function showAlertDialog(options: AlertDialogOptions): number {
  // Create a container element for the alert dialog
  const container = document.createElement('div')
  document.body.appendChild(container)

  // Generate a unique ID for this alert dialog
  const alertDialogId = alertDialogIdCounter++

  // Auto-close timer
  let autoCloseTimer: number | null = null

  // Create a promise to handle the retry and close events
  const actionPromise = new Promise<void>((resolve) => {
    // Create the alert dialog component
    const app = createApp({
      setup() {
        // Handle close event
        const handleClose = () => {
          // Clear auto-close timer if it exists
          if (autoCloseTimer) {
            clearTimeout(autoCloseTimer)
            autoCloseTimer = null
          }

          // Remove the alert dialog from the DOM
          app.unmount()
          document.body.removeChild(container)

          // Remove from active alert dialogs
          delete alertDialogs[alertDialogId]

          // Call the onClose callback if provided
          if (options.onClose) {
            options.onClose()
          }
        }

        // Handle retry event
        const handleRetry = () => {
          // Call the onRetry callback if provided
          if (options.onRetry) {
            options.onRetry()
          }

          // Resolve the promise
          resolve()
        }

        return () => h(AlertDialog, {
          title: options.title,
          message: options.message,
          type: options.type || 'error',
          showIcon: options.showIcon !== false,
          showRetryButton: options.showRetryButton || false,
          retryButtonText: options.retryButtonText || t('common.retry'),
          closeButtonText: options.closeButtonText || t('common.dismiss'),
          closeOnBackdrop: options.closeOnBackdrop !== false,
          onClose: handleClose,
          onRetry: handleRetry
        })
      }
    })

    // Mount the app to the container
    app.use(i18n)
    app.mount(container)

    // Store the alert dialog instance
    alertDialogs[alertDialogId] = {
      id: alertDialogId,
      el: container,
      close: () => {
        // Clear auto-close timer if it exists
        if (autoCloseTimer) {
          clearTimeout(autoCloseTimer)
          autoCloseTimer = null
        }

        app.unmount()
        if (document.body.contains(container)) {
          document.body.removeChild(container)
        }
        delete alertDialogs[alertDialogId]
      }
    }

    // Set up auto-close if enabled
    if (options.autoClose) {
      const duration = options.duration || 5000 // Default to 5 seconds
      autoCloseTimer = window.setTimeout(() => {
        if (alertDialogs[alertDialogId]) {
          alertDialogs[alertDialogId].close()
        }
      }, duration)
    }
  })

  return alertDialogId
}

/**
 * Close a specific alert dialog by ID
 */
function closeAlertDialog(id: number): void {
  if (alertDialogs[id]) {
    alertDialogs[id].close()
  }
}

/**
 * Close all active alert dialogs
 */
function closeAllAlertDialogs(): void {
  Object.values(alertDialogs).forEach(alertDialog => alertDialog.close())
}

/**
 * Show an error alert dialog
 */
function error(title: string, message: string, options: Partial<AlertDialogOptions> = {}): number {
  return showAlertDialog({
    title,
    message,
    type: 'error',
    ...options
  })
}

/**
 * Show a warning alert dialog
 */
function warning(title: string, message: string, options: Partial<AlertDialogOptions> = {}): number {
  return showAlertDialog({
    title,
    message,
    type: 'warning',
    ...options
  })
}

/**
 * Show an info alert dialog
 */
function info(title: string, message: string, options: Partial<AlertDialogOptions> = {}): number {
  return showAlertDialog({
    title,
    message,
    type: 'info',
    ...options
  })
}

/**
 * Show a network error alert dialog
 */
function networkError(title: string, message: string, options: Partial<AlertDialogOptions> = {}): number {
  return showAlertDialog({
    title,
    message,
    type: 'network',
    showRetryButton: true,
    ...options
  })
}

/**
 * Show an alert dialog for an AppError
 */
function showAppError(appError: AppError, options: Partial<AlertDialogOptions> = {}): number {
  // Determine if this is a critical error that should use an alert dialog
  const isCriticalError =
    appError.category === ErrorCategory.NETWORK ||
    appError.category === ErrorCategory.TIMEOUT ||
    appError.severity === ErrorSeverity.CRITICAL

  // Determine alert dialog type based on error category
  let type: 'error' | 'warning' | 'info' | 'network' = 'error'

  if (appError.category === ErrorCategory.NETWORK || appError.category === ErrorCategory.TIMEOUT) {
    type = 'network'
  } else if (appError.severity === ErrorSeverity.WARNING) {
    type = 'warning'
  } else if (appError.severity === ErrorSeverity.INFO) {
    type = 'info'
  }

  // Determine title based on error category
  let title = t('errors.errorOccurred')

  if (appError.category === ErrorCategory.NETWORK) {
    title = t('errors.networkErrorTitle')
  } else if (appError.category === ErrorCategory.TIMEOUT) {
    title = t('errors.timeoutErrorTitle')
  } else if (appError.category === ErrorCategory.SERVER) {
    title = t('errors.serverErrorTitle')
  }

  // Show the alert dialog
  return showAlertDialog({
    title,
    message: appError.message,
    type,
    showRetryButton: !!appError.retry,
    ...options
  })
}

export const alertDialogService = {
  show: showAlertDialog,
  close: closeAlertDialog,
  closeAll: closeAllAlertDialogs,
  error,
  warning,
  info,
  networkError,
  showAppError
}
